import * as tslib_1 from "tslib";
import { getCategoryUnits } from '../units';
import { ApplicationError } from '../../helpers';
/**
 * Unit conversion error type
 */
var UnitConversionError = /** @class */ (function (_super) {
    tslib_1.__extends(UnitConversionError, _super);
    function UnitConversionError() {
        return _super !== null && _super.apply(this, arguments) || this;
    }
    return UnitConversionError;
}(ApplicationError));
export { UnitConversionError };
/**
 * Asbtract unit converter class that has functionality for converting between units within a single category (length,
 * temperature, etc.). There is a subclass for each category (LengthConverter, TemperatureConverter, etc.) that defines
 * the conversion factors/methods for that category.
 */
var BaseConverter = /** @class */ (function () {
    /**
     * Constructor
     * @param {UnitCategory} _unitCategory Unit category of the units this converter is capable of converting.
     */
    function BaseConverter(_unitCategory) {
        this._unitCategory = _unitCategory;
        /**
         * Map of conversion factors from the base unit to other units. In case the unit conversion requires a formula rather
         * than a conversion factor, the _baseConversionMethods and _reverseConversionFunctions maps should be used instead.
         */
        this._conversionFactors = new Map();
        /**
         * Map of conversion functions from the base unit to other units. This is used when the unit conversion cannot be
         * expressed as a simple conversion factor (e.g., converting Celsius to Fahrenheit or vice-versa). For conversion
         * functions, both the base function (base unit to other units, B -> U) and the reverse function (U -> B) must be
         * defined separately.
         */
        this._baseConversionFunctions = new Map();
        /**
         * Map of conversion functions from a given unit to the base unit (this is the reverse mapping of
         * _baseConversionFunctions).
         */
        this._reverseConversionFunctions = new Map();
    }
    /**
     * Converts a value from one unit to another
     * @param {number} value Value to convert
     * @param {T} from Source unit
     * @param {T} to Destination unit
     * @return {number} Converted value
     */
    BaseConverter.prototype.convert = function (value, from, to) {
        var base = this._convertToBaseUnit(value, from);
        return this._convertFromBaseUnit(base, to);
    };
    /**
     * Checks to ensure that a conversion factor or formula has been defined for each unit in the category. For formulas,
     * ensures that both the forward and reverse conversion functions have been defined. This method should be called at
     * the end of the constructor of all derived types.
     * @private
     */
    BaseConverter.prototype._check = function () {
        for (var _i = 0, _a = getCategoryUnits(this._unitCategory); _i < _a.length; _i++) {
            var unitStr = _a[_i];
            var unit = unitStr;
            if (unit !== this._baseUnit && !(this._conversionFactors.has(unit)
                || (this._baseConversionFunctions.has(unit) && this._reverseConversionFunctions.has(unit)))) {
                throw new Error("Missing conversion factor/formula for unit \"" + unit + "\" in converter for category \"" + this._unitCategory + "\". "
                    + "The converter must define either a single conversion factor (in _conversionFactors) or a pair of "
                    + "conversion formulas (in _baseConversionFunctions and _reverseConversionFunctions) for each unit "
                    + "in the category.");
            }
        }
    };
    /**
     * Converts a value from a given unit to the base unit.
     * @param {number} value Value to convert (expressed in the unit specified by the unit parameter).
     * @param {T} unit Unit of the value.
     * @return {number} Converted value expressed in the base unit.
     * @private
     */
    BaseConverter.prototype._convertToBaseUnit = function (value, unit) {
        if (unit === this._baseUnit) {
            return value;
        }
        if (this._reverseConversionFunctions.has(unit)) {
            var formula = this._reverseConversionFunctions.get(unit);
            return formula(value);
        }
        if (this._conversionFactors.has(unit)) {
            var factor = this._conversionFactors.get(unit);
            return value / factor;
        }
        throw new UnitConversionError("No conversion factor or formula found for converting a value from unit \"" + unit
            + (" to the base unit \"" + this._baseUnit + "\"."));
    };
    /**
     * Converts a value from the base unit to a given unit.
     * @param {number} value Value to convert (expressed in the base unit).
     * @param {T} unit Unit the value should be converted to.
     * @return {number} Converted value (expressed in the unit specified by the unit parameter).
     * @private
     */
    BaseConverter.prototype._convertFromBaseUnit = function (value, unit) {
        if (unit === this._baseUnit) {
            return value;
        }
        if (this._baseConversionFunctions.has(unit)) {
            var formula = this._baseConversionFunctions.get(unit);
            return formula(value);
        }
        if (this._conversionFactors.has(unit)) {
            var factor = this._conversionFactors.get(unit);
            return value * factor;
        }
        throw new UnitConversionError("No conversion factor or formula found for converting a value from the base "
            + ("unit \"" + this._baseUnit + "\" to unit \"" + unit + "\"."));
    };
    return BaseConverter;
}());
export { BaseConverter };
